import { makeEndInstruction, Program, ProgramData } from "./program";
import { CalculationPlan, Question, SourceWithSourceQuestions, TaxReturnSubType } from "../../../../../swaggerServices";
import { Block, BlockType } from "./block";
import { Instruction } from "./instruction";
import { calculationSource, literalSource, makePlaceholderInstruction, startInstruction } from "./transient";
import { CalcInput, SubtypeMode, TaxReturnSourceOptions } from "./calc-input";
import { TypeSubtypeLookup } from "../../../../shared/util/tax-return-type-subtype";

export class CalcPlan {
  static fromData(data: CalculationPlan, sources: SourceWithSourceQuestions[], blocks: Block[], typeSubtypeLookup: TypeSubtypeLookup, isGlobalCalcPlan: boolean) {
    const programs: ProgramData[] = JSON.parse(data.plan) || [];
    const blocksMap = new Map();
    blocks.forEach(b => blocksMap.set(b.id, b));
    const sourcesMap = new Map();
    sources.forEach(s => sourcesMap.set(s.id.toString(), s));

    const calcPlan = new CalcPlan(data.id, data.question, 
      programs.map(p => Program.fromData(p, sourcesMap, blocksMap, true, typeSubtypeLookup, isGlobalCalcPlan)),
      isGlobalCalcPlan, data.skipTrace);

    if (calcPlan.programs.length === 0) {
      calcPlan.addNewProgram();
    }
    return calcPlan;
  }

  constructor(
    public id: number,
    public question: Question,
    public programs: Program[],
    public isGlobalCalcPlan: boolean,
    public skipTrace: boolean,
  ) {
  }

  maxId(): number {
    let maxId = 1;
    this.programs.forEach(p => {
      maxId = Math.max(maxId, p.maxId());
    });
    return maxId;
  }

  addNewProgram() {
    const program = new Program([startInstruction, makePlaceholderInstruction()]);
    this.programs.push(program);
    return program;
  }

  addInstructionAfter(program: Program, instructionId: number, onRight: boolean, block: Block, preloadedInputs?: CalcInput[]) {
    let idAlloc = this.maxId();
    const nextId = () => {
      idAlloc += 1;
      return idAlloc;
    };

    const isTerminal = block.type === BlockType.Value || block.type === BlockType.End;
    const instructionToAdd = new Instruction(
      nextId(),
      block.name,
      block,
      [],
      block.type === BlockType.Decision ? [] : undefined,
      block.type === BlockType.Decision ? [] : undefined,
      isTerminal ? 1 : undefined,
      isTerminal ? 1 : undefined,
      isTerminal ? 1 : undefined,
    );
    const container = this.findInstructionContainer(program.instructions, instructionId);
    if (container) {
      let modifiedContainer = container;
      const index = container.findIndex(i => i.id === instructionId);
      const target = container[index];
      if (target.block.type === BlockType.Decision) {
        if (instructionToAdd.block.type === BlockType.Decision) {
          // when adding a decision to a decision on right, we need target.yes to move to new.yes
          if (onRight) {
            instructionToAdd.yes = target.yes;
            target.yes = [instructionToAdd];
            instructionToAdd.no = [makeEndInstruction(nextId())];
          } else {
            instructionToAdd.no = target.no;
            target.no = [instructionToAdd];
            instructionToAdd.yes = [makeEndInstruction(nextId())];
          }
        } else {
          // when adding to decision->right, we need to add to yes[0]
          // when adding to decision->down, we need to add to no[0]
          const targetContainer = onRight ? target.yes : target.no;
          targetContainer.splice(0, 0, instructionToAdd);
          modifiedContainer = targetContainer;
        }
      } else {
        // when adding to non-decision, we need to append after container[index]
        if (instructionToAdd.block.type === BlockType.Decision) {
          // if we add a decision block, then the RHS needs to move to yes
          const removed = container.splice(index + 1, container.length - index + 1, instructionToAdd);
          const yesPath = removed.filter(i => i.block.type !== BlockType.Placeholder);
          instructionToAdd.yes = yesPath.length ? yesPath : [makeEndInstruction(nextId())];
          instructionToAdd.no = [makeEndInstruction(nextId())];
        } else {
          container.splice(index + 1, 0, instructionToAdd);
        }
      }
      // if adding a value before an END, delete the END
      if (instructionToAdd.block.type === BlockType.Value) {
        const endIndex = modifiedContainer.findIndex(i => i.block.type === BlockType.End);
        if (endIndex >= 0) {
          const endInstruction = modifiedContainer[endIndex];
          instructionToAdd.complexityPreparer = endInstruction.complexityPreparer;
          instructionToAdd.complexityReviewer1 = endInstruction.complexityReviewer1;
          instructionToAdd.complexityReviewer2 = endInstruction.complexityReviewer2;
          modifiedContainer.splice(endIndex, 1);
        }
      }
      if (program.instructions.length >= 3 && program.instructions[program.instructions.length - 1].block.type === BlockType.Placeholder) {
        program.instructions.pop();
        if (instructionToAdd.block.type !== BlockType.Value) {
          program.instructions.push(makeEndInstruction(nextId()));
        }
      }
    }
    if(preloadedInputs){
      instructionToAdd.inputs = [];
      preloadedInputs.forEach(input => {
        const newInput = new CalcInput(input.source, input.field, input.options, input.isGlobalCalcPlan)
        instructionToAdd.inputs.push(newInput)
      })
    }
  }

  deleteInstruction(program: Program, instructionId: number) {
    const container = this.findInstructionContainer(program.instructions, instructionId);
    if (container) {
      let idAlloc = this.maxId();
      const nextId = () => {
        idAlloc += 1;
        return idAlloc;
      };

      const index = container.findIndex(i => i.id === instructionId);
      const instruction = container[index];
      container.splice(index, 1);
      if (instruction.yes) {
        container.push.apply(container, instruction.yes);
      }
      const lastBlockType = container.length ? container[container.length - 1].block.type : null;
      if (lastBlockType && lastBlockType !== BlockType.Value && lastBlockType !== BlockType.End && lastBlockType !== BlockType.Decision) {
        if (lastBlockType === BlockType.Start) {
          container.push(makePlaceholderInstruction());
        } else {
          container.push(makeEndInstruction(nextId()));
        }
      } else if (container !== program.instructions && container.length === 0) {
        container.push(makeEndInstruction(nextId()));
      }
      if (program.instructions.length === 2 && program.instructions[program.instructions.length - 1].block.type === BlockType.End) {
        program.instructions.pop();
        program.instructions.push(makePlaceholderInstruction());
      }
    }
  }

  emptyOutProgram(program: Program) {
    const index = this.programs.indexOf(program);
    if (index >= 0) {
      program.instructions = [startInstruction, makePlaceholderInstruction()];
    }
  }

  deleteProgram(program: Program) {
    const index = this.programs.indexOf(program);
    if (index >= 0) {
      this.programs.splice(index, 1);
    }
  }

  toPlanJson() {
    // TODO(aaorn): Need to update this section to send type/subtype information to server
    const serializeInput = input => {
      if (input.source === literalSource) {
        return {
          source: 'literal',
          field: input.field,
        };
      } else if (input.source === calculationSource) {
        return {
          source: 'calculation',
          field: input.field,
        };
      } else {
        let sourceOptions = {};
        if (input.source.type === "taxReturn") {
          sourceOptions = this.serializeTaxReturnSourceOptions(input.options);
        } 
        return {
          ...sourceOptions,
          source: input.source.id.toString(),
          field: input.field.id,
        };
      }
    };
    const serializeInstructions  = instructions => instructions.filter(i => i.id >= 0).map(i => ({
        id: i.id,
        name: i.name,
        block: i.block.id,
        inputs: i.inputs.map(ii => serializeInput(ii)),
        ...(i.yes ? {yes: serializeInstructions(i.yes)} : {}),
        ...(i.no ? {no: serializeInstructions(i.no)} : {}),
        ...(i.complexityPreparer != null ? {complexityPreparer: i.complexityPreparer} : {}),
        ...(i.complexityReviewer1 != null ? {complexityReviewer1: i.complexityReviewer1} : {}),
        ...(i.complexityReviewer2 != null ? {complexityReviewer2: i.complexityReviewer2} : {}),
        ...(i.description && i.description !== i.block.description ? {description: i.description} : {}),
      }));
    return this.programs.map(p => ({instructions: serializeInstructions(p.instructions)}));
  }

  toPlanJsonString() {
    return JSON.stringify(this.toPlanJson());
  }

  // Serialised the options to be saved (More info: RS-916)
  // {"type": "typeId", "subtype": "subtypeId|'prior'", "chain": true, "py": true},
  private serializeTaxReturnSourceOptions(options: TaxReturnSourceOptions) {
    const result = {};
    if (!options) { return result; }

    if (options.priorYear) {
      result["py"] = true;
    }

    if (options.type) {
      result["type"] = options.type.id;
    }

    if (options.subtypeMode === SubtypeMode.Prior) {
      result["subtype"] = "prior";
    } else if (options.subtypeMode === SubtypeMode.Chain) {
      result["chain"] = true;
      result["subtype"] = options.subtypeChain[0].id;
    } else if (options.subtypeMode === SubtypeMode.Specific) {
      const subtype: TaxReturnSubType = options.subtypeValue;
      result["subtype"] = subtype.id;
    }

    return result;
  }

  private findInstructionContainer(instructions: Instruction[], instructionId: number) {
    const paths = [];
    const found = instructions.find(i => {
      if (i.yes) {
        paths.push(i.yes);
      }
      if (i.no) {
        paths.push(i.no);
      }
      return i.id === instructionId;
    });
    if (found) {
      return instructions;
    } else {
      for (const path of paths) {
        const container = this.findInstructionContainer(path, instructionId);
        if (container) {
          return container;
        }
      }
    }
  }
}
